import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FormDialog, Field } from "./form-dialog";
import { nameField, serialField } from "@/lib/validators";

const schema = z.object({
  name: nameField(120),
  category: z.enum(["body", "lens", "lighting", "support", "audio", "other"]),
  serial: serialField,
  status: z.enum(["available", "on-shoot", "service", "missing"]),
});
type Values = z.infer<typeof schema>;

export function NewGearDialog() {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", category: "body", serial: "", status: "available" },
  });
  return (
    <FormDialog
      trigger={<Button size="sm" className="gap-1.5"><Plus className="size-3.5" /> Add item</Button>}
      title="Add gear item"
      description="Track a camera body, lens, or accessory in the kit."
      submitLabel="Add item"
      isDirty={isDirty}
      onReset={() => reset()}
      onSubmit={() => new Promise<void>((resolve, reject) => {
        handleSubmit((v) => {
          toast.success("Gear added", { description: v.name });
          resolve();
        }, () => reject())();
      })}
    >
      <Field label="Name" htmlFor="gr-name" error={errors.name?.message}>
        <Input id="gr-name" placeholder="Sony A1" {...register("name")} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category" error={errors.category?.message}>
          <Select value={watch("category")} onValueChange={(v) => setValue("category", v as Values["category"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="body">Body</SelectItem>
              <SelectItem value="lens">Lens</SelectItem>
              <SelectItem value="lighting">Lighting</SelectItem>
              <SelectItem value="support">Support</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Status" error={errors.status?.message}>
          <Select value={watch("status")} onValueChange={(v) => setValue("status", v as Values["status"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="on-shoot">On shoot</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="missing">Missing</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Serial #" htmlFor="gr-serial" error={errors.serial?.message}>
        <Input id="gr-serial" placeholder="Optional" {...register("serial")} />
      </Field>
    </FormDialog>
  );
}
